import { useEffect, useRef, useState } from "react";

const PUBLIC_KEY = process.env.REACT_APP_SHIFT4_PUBLIC_KEY || "";
const SDK_URL = "https://js.shift4.com/shift4.js";

export function useShift4Card(containerId) {
    const [ready, setReady] = useState(false);
    const [configured] = useState(Boolean(PUBLIC_KEY));
    const [error, setError] = useState(null);
    const shift4Ref = useRef(null);
    const componentRef = useRef(null);

    useEffect(() => {
        if (!configured) return;
        let cancelled = false;

        const init = () => {
            try {
                shift4Ref.current = window.Shift4(PUBLIC_KEY);
                const component = shift4Ref.current.createComponent("card");
                component.mount(`#${containerId}`);
                if (!cancelled) {
                    componentRef.current = component;
                    setReady(true);
                }
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
        };

        const existing = document.getElementById("shift4-sdk");
        if (existing && window.Shift4) {
            init();
        } else if (!existing) {
            const s = document.createElement("script");
            s.id = "shift4-sdk";
            s.src = SDK_URL;
            s.onload = init;
            s.onerror = () => setError("Failed to load Shift4 SDK.");
            document.head.appendChild(s);
        } else {
            existing.addEventListener("load", init, { once: true });
        }

        return () => {
            cancelled = true;
            componentRef.current?.unmount?.();
            componentRef.current = null;
        };
    }, [containerId, configured]);

    const tokenize = async () => {
        if (!componentRef.current || !shift4Ref.current) throw new Error("Card form not ready.");
        const result = await shift4Ref.current.createToken(componentRef.current);
        if (result.error) throw new Error(result.error.message || "Card declined. Please try again.");
        return result.token.id;
    };

    return { ready, configured, error, tokenize };
}

export default function Shift4CardForm({ containerId = "shift4-card-container", className = "" }) {
    const { configured, error } = useShift4Card(containerId);

    if (!configured) {
        return (
            <div className={`rounded-lg border border-dashed border-white/20 p-6 text-center bg-black/20 ${className}`}>
                <div className="text-sm text-white/60 leading-relaxed">
                    Online payment coming soon.{" "}
                    <a href="tel:8623032001" className="text-orange-300 hover:underline">
                        Call (862) 303-2001
                    </a>{" "}
                    to pre-order or pay a deposit.
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            <div id={containerId} className="shift4-card-element" />
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
    );
}
